// src/components/ChannelPickerPanel/ChannelPickerPanel.jsx
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import ChannelTree from './ChannelTree';
import { useSelectedChannels } from '../../contexts/SelectedChannelsContext';
import { useSelectedSite } from '../../contexts/SelectedSiteContext';

export async function defaultLoadChannels({ backendUrl, user, siteId }) {
  const res = await axios.get(`${backendUrl}/channels/siteId/${siteId}`, {
    headers: { Authorization: `Bearer ${user.token}` },
  });
  return res.data ?? [];
}

export default function ChannelPickerPanel({
  backendUrl,
  user,
  loadChannels = defaultLoadChannels, // injection
  channelSnapshot,
  onLoadingChange,
  disabled = false,
}) {
  const { selectedSite } = useSelectedSite();     // ← from context
  const { setAvailableChannels } = useSelectedChannels();

  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setErr(null);

    // A restored view arrives with freshly fetched channels and its selection
    // already committed by the page. Do not clear that selection or fetch twice.
    if (channelSnapshot?.siteId === selectedSite) {
      setChannels(channelSnapshot.channels);
      setLoading(false);
      onLoadingChange?.(false);
    } else if (!selectedSite) {
      setChannels([]);
      setAvailableChannels([]);
      setLoading(false);
      onLoadingChange?.(false);
    } else {
      setChannels([]);
      setAvailableChannels([]);
      setLoading(true);
      onLoadingChange?.(true);
      (async () => {
        try {
          const list = await loadChannels({ backendUrl, user, siteId: selectedSite });
          if (cancelled) return;
          setChannels(list);
          setAvailableChannels(list);
        } catch (e) {
          if (cancelled) return;
          setErr(e?.message || 'Failed to load channels');
          setChannels([]);
          setAvailableChannels([]);
        } finally {
          if (!cancelled) {
            setLoading(false);
            onLoadingChange?.(false);
          }
        }
      })();
    }
    return () => { cancelled = true; };
  }, [selectedSite, backendUrl, user, loadChannels, setAvailableChannels, channelSnapshot, onLoadingChange]);

  const content = useMemo(() => {
    if (!selectedSite) return <div style={{ opacity: 0.7 }}>Choose a site to see channels.</div>;
    if (loading) return <div style={{ opacity: 0.7 }}>Loading channels…</div>;
    if (err) return <div style={{ color: 'tomato' }}>{String(err)}</div>;
    return <ChannelTree channels={channels} />;
  }, [selectedSite, loading, err, channels]);

  return (
    <fieldset disabled={disabled} style={{ display: 'grid', gap: 12, padding: 0, margin: 0, border: 0, minWidth: 0 }}>
      {content}
    </fieldset>
  );
}
